import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import API_BASE_URL from "../config";
import useCurrentUserAccess from "../hooks/useCurrentUserAccess";
import "./SalesPages.css";

function NewUser() {
  const navigate = useNavigate();
  const { loading: accessLoading, platformRole, accountRole } = useCurrentUserAccess();

  const isPlatformAdmin = platformRole?.key_name === "platform_admin";
  const isAccountAdmin = accountRole?.key_name === "account_admin";

  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);

  const [roleId, setRoleId] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [title, setTitle] = useState("");

  useEffect(() => {
    if (!accessLoading) {
      loadRoles();
    }
  }, [accessLoading, isPlatformAdmin, isAccountAdmin]);

  const pageMode = useMemo(() => {
    if (isPlatformAdmin) return "platform";
    if (isAccountAdmin) return "subscriber";
    return "none";
  }, [isPlatformAdmin, isAccountAdmin]);

  const filteredRoles = useMemo(() => {
    if (pageMode === "platform") {
      return roles.filter((role) => role.scope === "platform");
    }

    if (pageMode === "subscriber") {
      return roles.filter((role) => role.scope === "account");
    }

    return [];
  }, [roles, pageMode]);

  const pageTitle =
    pageMode === "platform" ? "New Platform User" :
    pageMode === "subscriber" ? "New User" :
    "New User";

  const pageSubtitle =
    pageMode === "platform"
      ? "Create an internal platform user and send an activation email."
      : pageMode === "subscriber"
      ? "Create a user for your account and send an activation email."
      : "You do not have access to create users here.";

  const setupHeading =
    pageMode === "platform" ? "Platform Access" : "User Setup";

  const setupDescription =
    pageMode === "platform"
      ? "Assign the new internal user to a platform role. They will receive an activation email to set their own password."
      : "Assign the new user to an account role. They will receive an activation email to set their own password.";

  const userScopeLabel =
    pageMode === "platform" ? "Platform User" :
    pageMode === "subscriber" ? "Subscriber User" :
    "Unavailable";

  const loadRoles = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/roles`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) return;

      const data = await res.json();
      setRoles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load roles error:", err.message);
    }
  };

  const handleSave = async () => {
    try {
      if (pageMode === "none") {
        alert("You do not have permission to create users.");
        return;
      }

      if (!roleId || !name.trim() || !email.trim()) {
        alert("Role, Name, and Email are required.");
        return;
      }

      setSaving(true);

      const token = localStorage.getItem("token");

      if (pageMode === "platform") {
        const res = await fetch(`${API_BASE_URL}/api/users/platform-user`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            platform_role_id: roleId,
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim() || null,
            title: title.trim() || null
          })
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || data.message || "Failed to create platform user");
        }

        if (data.activation_email_sent) {
          alert("Platform user created successfully. An activation email was sent.");
        } else {
          alert(
            `Platform user created successfully, but the activation email may not have sent.\n\nActivation URL:\n${data.activation_url || "Unavailable"}`
          );
        }

        navigate("/users");
        return;
      }

      if (pageMode === "subscriber") {
        const res = await fetch(`${API_BASE_URL}/api/users/account-user`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            role_id: roleId,
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim() || null,
            title: title.trim() || null
          })
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || data.message || "Failed to create subscriber user");
        }

        if (data.activation_email_sent) {
          alert("User created successfully. An activation email was sent.");
        } else {
          alert(
            `User created successfully, but the activation email may not have sent.\n\nActivation URL:\n${data.activation_url || "Unavailable"}`
          );
        }

        navigate("/users");
      }
    } catch (err) {
      console.error("Create user error:", err.message);
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (accessLoading) {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">Loading access...</div>
        </div>
      </>
    );
  }

  if (pageMode === "none") {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">
            You do not have access to create users.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">{pageTitle}</h1>
            <p className="sales-subtitle">{pageSubtitle}</p>
          </div>

          <div className="sales-header-actions">
            <button className="sales-secondary-btn" onClick={() => navigate("/users")}>
              Cancel
            </button>
            <button className="sales-primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? "Creating..." : "Create & Send Activation"}
            </button>
          </div>
        </div>

        <div className="sales-form-grid">
          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>{setupHeading}</h2>
              <p>{setupDescription}</p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field">
                <label>User Scope</label>
                <input value={userScopeLabel} readOnly />
              </div>

              <div className="sales-field">
                <label>{pageMode === "platform" ? "Platform Role" : "Role"}</label>
                <select value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                  <option value="">Select Role</option>
                  {filteredRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sales-field sales-field-full">
                <label>Activation Flow</label>
                <input
                  value="The user will receive an activation email and set their own password."
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>User Details</h2>
              <p>
                Enter the user’s basic account information. No password is needed.
              </p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field sales-field-full">
                <label>Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>Email</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>Phone</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>Title</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>

              <div className="sales-field sales-field-full">
                <label>Password</label>
                <input value="User will create their password from the activation email." readOnly />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NewUser;